// PWA — src/lib/swUpdate.ts — 2026-07-14 (PWA_SelfUpdate, pre-launch blocker)
//
// Before this, main.tsx registered the service worker and went silent:
// browsers only check for a new SW on navigation, and an installed PWA that
// stays open on a phone may never navigate — so a shipped update never
// arrived without uninstall/reinstall (+ a Cloudflare purge for the stale
// shell; fixed separately in public/_headers with no-cache on / and
// /index.html).
//
// This module wires the full update lifecycle:
//   1. POLL — registration.update() on foreground (visibilitychange/focus)
//      and every 20 minutes. Cheap: a conditional GET of /sw.js, which is
//      Cache-Control: no-cache.
//   2. The SW itself already calls skipWaiting() + clients.claim(), so a new
//      version takes control on its own → 'controllerchange' fires here.
//   3. APPLY — reload EXACTLY once (module-scope guard), and NEVER out from
//      under someone mid-action:
//        - app hidden/backgrounded → reload immediately (nothing can be
//          in-progress on screen; next foreground is the new build).
//        - app visible → never yank: show the "Update ready — tap to
//          refresh" banner AND auto-apply on the next background transition.
//      An in-progress reply can therefore never be lost: a visible session
//      is only reloaded by the owner's own tap or after they leave the app.
//   4. The FIRST controllerchange after a fresh install (claim() taking over
//      an uncontrolled page) is ignored — that's not an update.

/** Decide what to do when a NEW service worker takes control. Pure. */
export function decideUpdateAction(args: {
  /** Was the page already controlled before this change? false = first
   *  install claim — not an update, do nothing. */
  wasControlled: boolean;
  /** document.hidden at the moment of the change. */
  hidden: boolean;
}): 'ignore' | 'reload' | 'banner' {
  if (!args.wasControlled) return 'ignore';
  return args.hidden ? 'reload' : 'banner';
}

let reloading = false;
let pendingReload = false;

function applyReload(): void {
  if (reloading) return; // exactly-once guard — no reload loops
  reloading = true;
  window.location.reload();
}

const BANNER_ID = 'ss-update-banner';

function showUpdateBanner(): void {
  if (document.getElementById(BANNER_ID)) return;
  const bar = document.createElement('div');
  bar.id = BANNER_ID;
  bar.setAttribute('role', 'status');
  bar.style.cssText = [
    'position:fixed',
    'left:50%',
    'bottom:calc(72px + env(safe-area-inset-bottom, 0px))', // above the tab bar
    'transform:translateX(-50%)',
    'z-index:9999',
    'background:var(--ss-accent, #2563EB)',
    'color:#fff',
    'padding:10px 16px',
    'border-radius:9999px',
    'font:600 14px/1.2 system-ui, sans-serif',
    'box-shadow:0 4px 14px rgba(0,0,0,0.25)',
    'cursor:pointer',
    'border:none',
  ].join(';');
  bar.textContent = 'Update ready — tap to refresh';
  bar.addEventListener('click', () => applyReload());
  document.body.appendChild(bar);
}

/**
 * Wire the self-update lifecycle. Call once, after (or instead of) the plain
 * registration call, production only.
 */
export function wireSwUpdateLifecycle(): void {
  if (!('serviceWorker' in navigator)) return;

  let registration: ServiceWorkerRegistration | undefined;
  // Was this page controlled at wiring time? Distinguishes a real update's
  // controllerchange from the first-install claim().
  let wasControlled = Boolean(navigator.serviceWorker.controller);

  navigator.serviceWorker
    .register('/sw.js')
    .then((reg) => {
      registration = reg;
      reg.addEventListener('updatefound', () => {
        console.log('[sw-update] new service worker installing');
      });
    })
    .catch((err) => {
      console.warn('SW registration failed', err);
    });

  const checkForUpdate = () => {
    // Conditional GET of /sw.js — no-op when nothing changed.
    void registration?.update().catch(() => undefined);
  };

  // Poll: on foreground, on focus, and every 20 minutes while open.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      checkForUpdate();
    } else if (pendingReload) {
      // The owner left the app with an update pending — apply it now, while
      // nothing on screen can be interrupted. Next open is the new build.
      applyReload();
    }
  });
  window.addEventListener('focus', checkForUpdate);
  window.setInterval(checkForUpdate, 20 * 60 * 1000);

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    const action = decideUpdateAction({ wasControlled, hidden: document.hidden });
    wasControlled = true; // any subsequent change is a real update
    if (action === 'reload') {
      applyReload();
    } else if (action === 'banner') {
      pendingReload = true;
      showUpdateBanner();
    }
  });
}
